import { VehicleType } from "@daytrip/legacy-enums";
import type { Country, SimpleDriver, SimpleUser, User } from "@daytrip/legacy-models";
import { RetrieveDriversOptions } from "@daytrip/legacy-options";
import { isNotUndefinedOrNull } from "@daytrip/utils";
import autobind from "autobind-decorator";
import { plainToClass } from "class-transformer";
import { validate } from "class-validator";
import isEmpty from "lodash/isEmpty";
import { action, computed, observable } from "mobx";

import { PageStore } from "../../stores/PageStore";

import type { DriversMapPageRouter } from "./DriversMapPageRouter";

@autobind
export class DriversMapPageStore extends PageStore<DriversMapPageRouter, null> {
    @observable
    public drivers?: Array<SimpleDriver & { vehicleTypes: VehicleType[] }>;

    @observable
    public countries?: Array<Country>;

    @observable
    public managers?: Array<SimpleUser>;

    @computed
    public get filteredDrivers(): Array<SimpleDriver> | undefined {
        if (!this.drivers) {
            return undefined;
        }

        const vehicleTypes = this.pageRouter.vehicleTypes;
        if (vehicleTypes?.length) {
            return this.drivers.filter((driver) => driver.vehicleTypes.some((type) => vehicleTypes.includes(type)));
        }

        return this.drivers;
    }

    @action
    public async onFetchData() {
        await Promise.all([this.fetchContent(), this.fetchDrivers()]);
    }

    @action
    public async fetchContent() {
        [this.countries, this.managers] = await Promise.all([
            this.rpcClient.content.retrieveCountries({}),
            this.rpcClient.user.getSimpleRegionalManagers(),
        ]);
    }

    @action
    public async fetchDrivers(): Promise<void> {
        this.drivers = undefined;
        const options = plainToClass(RetrieveDriversOptions, {
            isDriver: true,
            isCompanyDriver: true,
            isDriversCompany: false,
            searchString: this.pageRouter.search,
            managerIds: this.pageRouter.managerIds,
            countryIds: this.pageRouter.countryIds,
            statusIn: this.pageRouter.statuses,
        });

        // Too many drivers.
        if (!options.searchString && isEmpty(options.managerIds) && isEmpty(options.countryIds)) {
            this.drivers = [];
            return undefined;
        }

        const optionsValidationErrors = await validate(options, {
            skipMissingProperties: true,
            forbidUnknownValues: false,
        });
        if (optionsValidationErrors.length > 0) {
            return Promise.reject("Validation error");
        }
        this.drivers = await this.rpcClient.driver.retrieveSimpleDriversForDriversMap(options);
    }

    public isDataFetched(): this is DriversMapPageStore & DriversMapPageStoreDataFetched {
        return (
            isNotUndefinedOrNull(this.countries) &&
            isNotUndefinedOrNull(this.drivers) &&
            isNotUndefinedOrNull(this.managers)
        );
    }
}

export interface DriversMapPageStoreDataFetched {
    countries: Array<Country>;
    drivers: Array<User>;
    managers: Array<SimpleUser>;
}
